/* ============================================================================
   MOTION BRIDGE — the Homepage prototype's motion.dev setup, as a module the
   PDP's scripts share. Same pinned build as figs-nav (one instance on the
   page), same token read from the --mo-* custom properties, same tween()/
   track() pair so the lab bar's Speed and Reduced controls reach every
   animation.
   ========================================================================== */

import { animate, inView, stagger, hover, press } from 'https://cdn.jsdelivr.net/npm/motion@12.23.12/+esm';

export { animate, inView, stagger, hover, press };

const CS = getComputedStyle(document.documentElement);
const raw = (n) => CS.getPropertyValue(n).trim();
/* each token falls back to its value in the Homepage / PDP token block, so
   a module that rides on this bridge (the shared embroidery configurator,
   opened from the bag on any page) still moves on pages that don't declare
   the --mo-* tokens — the PLPs and the Homepage */
const sec = (n, d) => { const v = raw(n); if (!v) return d; return v.endsWith('ms') ? parseFloat(v) / 1000 : parseFloat(v); };
const px = (n, d) => { const v = parseFloat(raw(n)); return Number.isFinite(v) ? v : d; };
const num = px;
const ease = (n, d) => { const m = raw(n).match(/-?[\d.]+/g); return m && m.length === 4 ? m.map(Number) : d; };

export const MO = {
  instant: sec('--mo-instant', 0.1), quick: sec('--mo-quick', 0.2), base: sec('--mo-base', 0.32),
  slow: sec('--mo-slow', 0.56), deliberate: sec('--mo-deliberate', 0.88), cinematic: sec('--mo-cinematic', 1.4),
  eStandard: ease('--mo-ease-standard', [0.4, 0, 0.2, 1]), eEntrance: ease('--mo-ease-entrance', [0.16, 1, 0.3, 1]),
  eExit: ease('--mo-ease-exit', [0.7, 0, 0.84, 0]), eEmphasis: ease('--mo-ease-emphasis', [0.83, 0, 0.17, 1]),
  eSpring: ease('--mo-ease-spring', [0.34, 1.56, 0.64, 1]),
  riseXs: px('--mo-rise-xs', 8), riseSm: px('--mo-rise-sm', 16), riseMd: px('--mo-rise-md', 32), riseLg: px('--mo-rise-lg', 64),
  stagTight: sec('--mo-stagger-tight', 0.04), stagBase: sec('--mo-stagger-base', 0.08), stagLoose: sec('--mo-stagger-loose', 0.14),
  scaleHover: num('--mo-scale-hover', 1.02), scaleIn: num('--mo-scale-in', 1.04), scalePress: num('--mo-scale-press', 0.97),
};

/* Shared, mutable review state. `reduced` starts from the OS setting; the lab
   bar's Reduced toggle flips it and rebuilds. */
export const state = {
  reduced: matchMedia('(prefers-reduced-motion: reduce)').matches,
  speed: 1,
};

const tracked = new Set();
export const teardown = [];

export function track(c) {
  if (!c || typeof c !== 'object') return c;
  if (!('speed' in c) && typeof c.stop !== 'function') return c;
  tracked.add(c);
  try { c.speed = state.speed; } catch (e) { /* not every control exposes speed */ }
  Promise.resolve(c.finished ?? c).then(() => tracked.delete(c)).catch(() => tracked.delete(c));
  return c;
}

export function setSpeed(v) {
  state.speed = v;
  tracked.forEach((c) => { try { c.speed = v; } catch (e) { /* ignore */ } });
}

export function clearTracked() { tracked.clear(); }

/* Reveal/entrance wrapper. Under Reduced, travel and scale collapse to their
   end values and only opacity / clip-path still resolve — reduced motion is a
   design state, not an off switch. */
export function tween(el, kf, opts = {}) {
  if (!el || (Array.isArray(el) && !el.length)) return undefined;
  if (state.reduced) {
    const flat = {};
    for (const [k, v] of Object.entries(kf)) {
      flat[k] = k === 'opacity' || k === 'clipPath' ? v : Array.isArray(v) ? v[v.length - 1] : v;
    }
    return track(animate(el, flat, { duration: MO.instant, delay: 0, ease: 'linear' }));
  }
  return track(animate(el, kf, opts));
}

/* Direct, tracked animate() for interaction feedback. Under Reduced it keeps
   the end state and drops the travel. */
export function play(el, kf, opts = {}) {
  if (!el) return undefined;
  if (state.reduced) {
    const flat = {};
    for (const [k, v] of Object.entries(kf)) flat[k] = Array.isArray(v) ? v[v.length - 1] : v;
    return track(animate(el, flat, { duration: MO.instant, ease: 'linear' }));
  }
  return track(animate(el, kf, opts));
}

/* Scroll reveals — Homepage recipes. Elements that enter view in the same
   frame reveal as one batch, ordered row by row (the PLP's grid-stagger
   batching), so a row reads as one gesture and a tall mobile stack still
   reveals piece by piece. `startDelay` may be a function, read when the batch
   flushes (the PDP queues first-screen batches behind its title). Shared by
   the PDP and the Uniform Builder (15). */
export function byPosition(a, b) {
  const ra = a.getBoundingClientRect();
  const rb = b.getBoundingClientRect();
  return Math.abs(ra.top - rb.top) > 8 ? ra.top - rb.top : ra.left - rb.left;
}

export function revealOnView(els, kf, { duration, stag = MO.stagTight, startDelay = 0, margin = '0px 0px -10% 0px' }) {
  let queue = [];
  let raf = 0;
  const flush = () => {
    raf = 0;
    const batch = queue.sort(byPosition);
    queue = [];
    const delay = typeof startDelay === 'function' ? startDelay() : startDelay;
    tween(batch, kf, { duration, ease: MO.eEntrance, delay: stagger(stag, { startDelay: delay }) });
  };
  els.forEach((el) => teardown.push(inView(el, () => {
    queue.push(el);
    if (!raf) raf = requestAnimationFrame(flush);
  }, { margin })));
}

/* The one spring the system licenses: affirmative, user-initiated moments
   (add-to-bag, favourite). */
export const SPRING_POP = { type: 'spring', stiffness: 520, damping: 14 };
export const SPRING_RELEASE = { type: 'spring', stiffness: 480, damping: 18 };

export const $ = (s, r = document) => r.querySelector(s);
export const $$ = (s, r = document) => [...r.querySelectorAll(s)];
export const mqDesktop = matchMedia('(min-width: 780px)');
